import { useTheme } from "@salt-ds/core";
import { useEffect, useId, useState } from "react";

export interface MermaidProps {
  /** Mermaid diagram source */
  chart: string;
}

/**
 * Renders a Mermaid diagram in an MDX document. Fenced code blocks with the
 * language "mermaid" are converted to this component at build time (see
 * scripts/remark-mermaid.ts). Mermaid is loaded on demand, so pages without
 * diagrams don't pay for it.
 */
export const Mermaid = ({ chart }: MermaidProps) => {
  const { mode } = useTheme();
  const id = `mermaid-${useId().replace(/[^\w-]/g, "")}`;
  const [svg, setSvg] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    import("mermaid")
      .then(async ({ default: mermaid }) => {
        mermaid.initialize({
          startOnLoad: false,
          theme: mode === "dark" ? "dark" : "default",
        });
        const { svg } = await mermaid.render(id, chart);
        if (!cancelled) {
          setSvg(svg);
          setError(undefined);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [chart, id, mode]);

  if (error) {
    return (
      <pre className="vuuMermaid vuuMermaid-error">
        {`Mermaid diagram could not be rendered: ${error}\n\n${chart}`}
      </pre>
    );
  }

  return (
    <div
      className="vuuMermaid"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: svg generated by mermaid from document source
      dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
    />
  );
};
